/* NZS Scribble — hand-drawn SVG highlight decorator.
   Ported from the NZPerf concept theme's highlighted-text / icon-scribble system
   (snippets/highlighted-text.liquid + snippets/icon-scribble.liquid + the stroke-dasharray
   self-draw at concept theme.css 11465-11602): same three path shapes (circle,
   basic-underline, squiggle-underline), same one-shot IntersectionObserver draw trigger,
   rebuilt as a plain attribute decorator (no highlighted-text custom element / no per-section
   markup surgery beyond one data attribute).

   Usage:
     <em data-nzs-scribble="circle">Potential</em>
       -> no data-nzs-scribble-word: the WHOLE element's text is wrapped. Use this on a target
          that already isolates the right text on its own (an <em>, a short eyebrow <span>, a
          two-word heading) — this is how every Batch-A anchor uses it.
     <span data-nzs-scribble="underline" data-nzs-scribble-word="Save">Stack &amp; Save</span>
       -> data-nzs-scribble-word="X": wraps only the first case-insensitive match of word X
          inside the element's text, leaving the rest untouched. Kept for future editor-text
          targets (per the port plan's "small config map" case) — not exercised by the four
          Batch-A anchors, which all use whole-element mode.
     <h1 data-nzs-scribble="underline" data-nzs-scribble-color="white">Collection</h1>
       -> data-nzs-scribble-color="white": white stroke (for scribbles over dark hero imagery).
          Default stroke is the brand blue token (--blue-500).

   Guards:
     - Draws once per page load (IntersectionObserver, threshold 0.4, unobserve after firing —
       never re-triggers on re-scroll).
     - prefers-reduced-motion: reduce -> scribble renders fully drawn immediately (static), never
       missing, never animates.
     - No IntersectionObserver support -> same static fallback.
     - Word not found (e.g. merchant edited the copy) -> fails safe to whole-element wrap instead
       of silently doing nothing.
*/
(function () {
  if (window.__nzsScribbleInit) return;
  window.__nzsScribbleInit = true;

  var ICONS = {
    circle: {
      viewBox: '0 0 800 350',
      strokeWidth: '20',
      transform: 'matrix(0.97913,0,0,0.97913,400,179)',
      d: 'M253,-161 C253,-161 -284.789,-201.46 -376,-21 C-469,163 67.623,174.21 256,121 C564,34 250.829,-141.693 19.107,-116.936'
    },
    underline: {
      viewBox: '-400 -55 730 60',
      strokeWidth: '10',  /* screen px (vector-effect:non-scaling-stroke) -- 30 drew a slab under hero titles */
      d: 'm -383.25 -6 c 55.25 -22 130.75 -33.5 293.25 -38 c 54.5 -0.5 195 -2.5 401 15'
    },
    squiggle: {
      viewBox: '-347 -30.1947 694 96.19',
      strokeWidth: '20',
      d: 'M-335,54 C-335,54 -171,-58 -194,-3 C-217,52 -224.12,73.552 -127,11 C-68,-27 -137,50 -33,42 C31.439,37.043 147.147,-29.308 335,2'
    }
  };

  var INLINE_TAGS = { EM: 1, SPAN: 1, A: 1, I: 1, B: 1, STRONG: 1 };
  var SVG_NS = 'http://www.w3.org/2000/svg';
  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var io = null;

  function buildSvg(type) {
    var def = ICONS[type];
    if (!def) return null;
    var svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('viewBox', def.viewBox);
    svg.setAttribute('class', 'nzs-scribble__svg nzs-scribble__svg--' + type);
    svg.setAttribute('preserveAspectRatio', 'none');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    var path = document.createElementNS(SVG_NS, 'path');
    path.setAttribute('d', def.d);
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-linejoin', 'round');
    path.setAttribute('stroke-width', def.strokeWidth);
  path.setAttribute('fill', 'none');
    path.setAttribute('pathLength', '1');
    if (def.transform) path.setAttribute('transform', def.transform);
    svg.appendChild(path);
    return svg;
  }

  function observe(el) {
    if (!('IntersectionObserver' in window)) {
      el.classList.add('nzs-scribble--static');
      return;
    }
    if (!io) {
      io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('animate');
          io.unobserve(entry.target);
        });
      }, { threshold: 0.4 });
    }
    io.observe(el);
  }

  function decorate(el, type, color) {
    if (!el || el.__nzsScribbleDone) return;
    el.__nzsScribbleDone = true;
    el.classList.add('nzs-scribble', 'nzs-scribble--' + type);
    if (INLINE_TAGS[el.tagName]) el.classList.add('nzs-scribble--inline');
    if (color === 'white') el.classList.add('nzs-scribble--on-dark');
    var svg = buildSvg(type);
    if (!svg) return;
    el.appendChild(svg);
    if (reduced) {
      el.classList.add('nzs-scribble--static');
      return;
    }
    observe(el);
  }

  function decorateWord(root, word, type, color) {
    var lower = word.toLowerCase();
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
    var node;
    while ((node = walker.nextNode())) {
      var idx = node.nodeValue.toLowerCase().indexOf(lower);
      if (idx === -1) continue;
      var before = node.nodeValue.slice(0, idx);
      var match = node.nodeValue.slice(idx, idx + word.length);
      var after = node.nodeValue.slice(idx + word.length);
      var wrap = document.createElement('span');
      wrap.textContent = match;
      var frag = document.createDocumentFragment();
      if (before) frag.appendChild(document.createTextNode(before));
      frag.appendChild(wrap);
      if (after) frag.appendChild(document.createTextNode(after));
      node.parentNode.replaceChild(frag, node);
      decorate(wrap, type, color);
      return true;
    }
    return false;
  }

  function init() {
    var els = document.querySelectorAll('[data-nzs-scribble]');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      var type = el.getAttribute('data-nzs-scribble');
      if (!ICONS[type]) continue;
      var word = el.getAttribute('data-nzs-scribble-word');
      var color = el.getAttribute('data-nzs-scribble-color');
      if (word) {
        var found = decorateWord(el, word, type, color);
        if (!found) decorate(el, type, color); // fail-safe: word missing -> whole element
      } else {
        decorate(el, type, color);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
